import * as React from "react";
import {useMemo, useState} from "react";
import {IPlansTabResponse} from "../../../../api/AdminAnalyticsApi";
import {applySort, SortDir} from "../../../../utils/listUtil";
import {sortTh} from "../shared/SortableTh.component";
import {PLAN_ENGAGEMENT_DESCRIPTION} from "../shared/analytics.constants";
import {PlanDrilldownTable} from "./PlanDrilldownTable.component";
import {usePlanDrilldown} from "./usePlanDrilldown";
import {numberWithCommas} from "../../../../utils/numberUtil";

interface IPlanEngagementTableProps {
    rows: IPlansTabResponse["planEngagement"];
    planViewTab: "client" | "internal";
    range: { startDate: string; endDate: string };
    country: string;
}

export const PlanEngagementTable: React.FC<IPlanEngagementTableProps> = (props) => {
    const [planSearchText, setPlanSearchText] = useState<string>("");
    const [planEngagementSort, setPlanEngagementSort] = useState<{ col: string; dir: SortDir }>({
        col: "visits",
        dir: "desc",
    });
    const [showAllPlans, setShowAllPlans] = useState<boolean>(false);
    const drilldown = usePlanDrilldown(props.range, props.country);

    const filteredPlanRows = useMemo(() => {
        const text = planSearchText.trim().toLowerCase();
        const filtered = text
            ? props.rows.filter((row) => row.planName.toLowerCase().includes(text)
                || row.topArea.toLowerCase().includes(text))
            : props.rows;
        return applySort(filtered, planEngagementSort.col, planEngagementSort.dir);
    }, [props.rows, planSearchText, planEngagementSort]);

    const shownCount = Math.min(showAllPlans ? filteredPlanRows.length : 20, filteredPlanRows.length);

    return (
        <section className="admin-analytics__section">
            <div className="admin-analytics__section-header">
                <h2 className="admin-analytics__section-title">Plan Engagement</h2>
                <span className="admin-analytics__section-subtitle">
                    {props.planViewTab === "internal" ? "Internal-only plans" : "Client-facing plans"}
                </span>
            </div>
            <p className="admin-analytics__section-desc">{PLAN_ENGAGEMENT_DESCRIPTION}</p>
            <div className="admin-analytics__table-controls">
                <input className="admin-analytics__input" placeholder="Filter plans" value={planSearchText}
                       onChange={(e) => {
                           setPlanSearchText(e.target.value);
                           setShowAllPlans(false);
                       }}/>
                <div className="admin-analytics__count-label">
                    Showing {shownCount} of {filteredPlanRows.length} plans
                </div>
            </div>
            <table className="admin-analytics__table">
                <thead>
                <tr>
                    {sortTh("planName", "Plan", planEngagementSort, setPlanEngagementSort, undefined, "asc")}
                    {sortTh("visits", "Visits", planEngagementSort, setPlanEngagementSort,
                        "admin-analytics__cell--right")}
                    {sortTh("visitShare", "Visit Share", planEngagementSort, setPlanEngagementSort,
                        "admin-analytics__cell--right")}
                    {sortTh("topArea", "Top Area", planEngagementSort, setPlanEngagementSort, undefined, "asc")}
                </tr>
                </thead>
                <tbody>
                {filteredPlanRows.length === 0 ? (
                    <tr>
                        <td colSpan={4} className="admin-analytics__empty-row">No plans match your filter.
                        </td>
                    </tr>
                ) : (showAllPlans ? filteredPlanRows : filteredPlanRows.slice(0, 20)).map((row) => (
                    <React.Fragment key={`${row.planName}-${row.topArea}`}>
                        <tr
                            className={`admin-analytics__row--expandable${
                                drilldown.expanded === row.planName ? " admin-analytics__row--expanded" : ""}`}
                            onClick={() => drilldown.toggle(row.planName)}
                            title="Click to see all pages visited"
                        >
                            <td>
                                <i className={`far ${
                                    drilldown.expanded === row.planName ? "fa-chevron-down" : "fa-chevron-right"
                                } admin-analytics__expand-icon`}
                                   aria-hidden="true"/>
                                {row.planName}
                            </td>
                            <td className="admin-analytics__cell--right">{numberWithCommas(row.visits)}</td>
                            <td className="admin-analytics__cell--right">{row.visitShare}%</td>
                            <td>{row.topArea}</td>
                        </tr>
                        {drilldown.expanded === row.planName && (
                            <tr className="admin-analytics__drilldown-row">
                                <td colSpan={4}>
                                    <PlanDrilldownTable
                                        data={drilldown.data}
                                        loading={drilldown.loading}
                                        colSpan={4}
                                        loadingMessage="Loading plan pages..."
                                    />
                                </td>
                            </tr>
                        )}
                    </React.Fragment>
                ))}
                </tbody>
            </table>
            {filteredPlanRows.length > 20 && (
                <button className="admin-analytics__show-more-btn"
                        onClick={() => setShowAllPlans(!showAllPlans)}>
                    {showAllPlans ? `Show Top 20` : `Show All ${filteredPlanRows.length} Plans`}
                </button>
            )}
        </section>
    );
};
